import { Ligne_Document_Vente } from '../models/models';
import { TypeLigne } from '../constants/enums';

/**
 * Synthèse informative de la VALEUR des ajustements d'un document d'inventaire.
 *
 * Chaque ligne ARTICLE d'un inventaire est un relevé de comptage (signe 0, inerte).
 * Le backend génère une ligne AJUSTEMENT (écart = comptée − théorique) portée comme
 * un vrai mouvement de stock, valorisée au coût (`prix_revient`, snapshoté à la
 * saisie ; fallback sur `article.prix_r` pour les lignes anciennes).
 *
 * Cette synthèse ne modifie rien : elle éclaire l'opérateur sur ce que représentent
 * les écarts en valeur — comme `computeCoutFabrication` valorise les composants.
 *
 *   écart > 0 → EXCÉDENT (boni)   → gain potentiel
 *   écart < 0 → MANQUANT (mali)   → démarque / perte
 *   net       → boni − mali (signé)
 *
 * Fonction pure : sûre dans un useMemo.
 */
export interface ValeurInventaireSynthese {
  /** Valeur cumulée des excédents (écart > 0), en positif */
  valeurBoni: number;
  /** Valeur cumulée des manquants (écart < 0), en positif (magnitude) */
  valeurMali: number;
  /** Ajustement net signé = valeurBoni − valeurMali */
  valeurNette: number;
  /** Nombre de lignes en excédent / en manquant */
  nbBoni: number;
  nbMali: number;
  /**
   * Détail par ligne ARTICLE de comptage — accessible via l'id de cette ligne
   * (les lignes AJUSTEMENT y sont rattachées via composant_de_ligne_id).
   * Une ligne sans écart ne figure pas dans la map.
   */
  parLigne: Map<number, { ecart: number; valeur: number; coutUnitaire: number }>;
}

export function computeValeurInventaire(
  lignes: Ligne_Document_Vente[],
): ValeurInventaireSynthese {
  const parLigne = new Map<number, { ecart: number; valeur: number; coutUnitaire: number }>();
  let valeurBoni = 0;
  let valeurMali = 0;
  let nbBoni = 0;
  let nbMali = 0;

  for (const l of lignes) {
    if (l.type_ligne !== TypeLigne.AJUSTEMENT || l.composant_de_ligne_id == null) continue;

    const cout      = Number(l.prix_revient ?? l.article?.prix_r ?? 0);
    const ecart     = Number(l.quantite ?? 0) * Number(l.signe ?? 0);   // signé
    const valeur    = ecart * cout;                                     // signé

    parLigne.set(l.composant_de_ligne_id, { ecart, valeur, coutUnitaire: cout });

    if (valeur >= 0) { valeurBoni += valeur;        nbBoni++; }
    else             { valeurMali += Math.abs(valeur); nbMali++; }
  }

  return {
    valeurBoni,
    valeurMali,
    valeurNette: valeurBoni - valeurMali,
    nbBoni,
    nbMali,
    parLigne,
  };
}
